import { TILE } from '../config/config';
import type { World } from '../game/world';
import type { Renderer } from '../gfx/renderer';
import type { MoverDef } from '../world/leveldata';
import type { SolidBox } from '../world/physics';

/**
 * Moving platform. Ping-pongs between two points; the player is carried along when
 * riding it (see Physics/`MoveResult.riding`).
 */
export class Mover implements SolidBox {
  x: number;
  y: number;
  w: number;
  h = 5;
  vx = 0;
  vy = 0;
  oneWay = true;
  private homeX: number;
  private homeY: number;
  private t: number;
  private readonly period: number;
  private readonly span: number;

  constructor(private def: MoverDef) {
    this.homeX = def.x * TILE;
    this.homeY = def.y * TILE;
    this.x = this.homeX;
    this.y = this.homeY;
    this.w = def.w * TILE;
    this.span = def.range * TILE;
    this.period = (this.span * 2) / Math.max(1, def.speed);
    this.t = (def.phase ?? 0) * this.period;
  }

  reset(): void {
    this.t = (this.def.phase ?? 0) * this.period;
    this.x = this.homeX;
    this.y = this.homeY;
    this.vx = 0;
    this.vy = 0;
  }

  update(dt: number): void {
    this.t = (this.t + dt) % this.period;
    // triangle wave 0..1..0
    const half = this.period / 2;
    const f = this.t < half ? this.t / half : 2 - this.t / half;
    const nx = this.def.axis === 'x' ? this.homeX + this.span * f : this.homeX;
    const ny = this.def.axis === 'y' ? this.homeY + this.span * f : this.homeY;
    this.vx = dt > 0 ? (nx - this.x) / dt : 0;
    this.vy = dt > 0 ? (ny - this.y) / dt : 0;
    this.x = nx;
    this.y = ny;
  }

  draw(r: Renderer, world: World): void {
    const tiles = Math.max(1, Math.round(this.w / TILE));
    for (let i = 0; i < tiles; i++) {
      r.blit(r.sprites.get(`tile.${world.theme}.plank`), this.x + i * TILE, this.y);
    }
  }
}
